import { createContext } from 'react';

/**
 * Appointment lifecycle state - tracking call start and participant status
 *
 * This group handles:
 * - When did the call start? (momentBothParticipantsJoined)
 * - How many participants are in the call? (participantCount)
 * - Is the appointment record being created? (isCreatingAppointment)
 * - Has the appointment been created successfully? (isAppointmentCreated)
 */
export interface AppointmentLifecycleState {
  /**
   * The timestamp when both participants joined the call.
   * Set from AppointmentEntity.createdAt via UPSERT operation.
   * Preserved on provider rejoin to maintain accurate session duration.
   */
  momentBothParticipantsJoined: Date | null;

  /**
   * Whether the appointment record creation is in progress
   */
  isCreatingAppointment: boolean;

  /**
   * Whether the appointment record has been successfully created
   */
  isAppointmentCreated: boolean;

  /**
   * Current number of participants in the Daily.co call
   */
  participantCount: number;

  /**
   * Computed: Whether we have a valid start time for duration tracking
   */
  hasStartTime: boolean;
}

/**
 * Duration state and actions - tracking and editing call duration
 *
 * This group handles:
 * - What's the current calculated duration? (calculatedDuration)
 * - Has the provider manually edited the duration? (editedDuration)
 * - What's the final duration to use? (finalDuration)
 * - How do we calculate/edit duration? (calculateDuration, editDuration)
 */
export interface DurationState {
  /**
   * Duration calculated from start time to current time (in minutes)
   */
  calculatedDuration: number;

  /**
   * Manually edited duration (in minutes), null if not edited
   */
  editedDuration: number | null;

  /**
   * Reason for manual duration edit (required for audit trail)
   */
  editReason: string | null;

  /**
   * Computed: Final duration to use (editedDuration ?? calculatedDuration)
   */
  finalDuration: number;

  /**
   * Calculate duration from start time to current time
   * Updates calculatedDuration state
   */
  calculateDuration: () => void;

  /**
   * Manually edit the duration with a reason (for audit trail)
   * @param minutes - New duration in minutes
   * @param reason - Reason for manual edit (e.g., "Technical Difficulties")
   */
  editDuration: (minutes: number, reason: string) => void;
}

/**
 * UI state and actions - managing modals and user interactions
 *
 * This group handles:
 * - Should we show the time limit modal? (showTimeLimitModal)
 * - Has the time limit notification been shown already? (timeLimitShown)
 * - How do we dismiss modals? (dismissTimeLimitModal)
 */
export interface UIState {
  /**
   * Whether the time limit notification has been shown (prevents duplicate notifications)
   */
  timeLimitShown: boolean;

  /**
   * Whether to currently show the time limit modal
   */
  showTimeLimitModal: boolean;

  /**
   * Dismiss the time limit modal
   */
  dismissTimeLimitModal: () => void;
}

/**
 * Complete provider call context value
 *
 * Organized into three logical groups for better maintainability:
 * - lifecycle: Appointment creation and participant tracking
 * - duration: Time calculation and manual editing
 * - ui: Modal state and user interactions
 *
 * @example
 * ```typescript
 * const { lifecycle, duration, ui } = useProviderCallContext();
 *
 * // Access specific domains
 * if (lifecycle.hasStartTime) {
 *   duration.calculateDuration();
 * }
 *
 * if (ui.showTimeLimitModal) {
 *   ui.dismissTimeLimitModal();
 * }
 * ```
 */
export interface ProviderCallContextValue {
  lifecycle: AppointmentLifecycleState;
  duration: DurationState;
  ui: UIState;
}

/**
 * React Context for provider call state management.
 *
 * This context provides centralized state for:
 * - Appointment creation when both participants join
 * - Duration calculation and manual editing
 * - Time limit notifications
 *
 * Must be used within ProviderCallContextController.
 *
 * @see ProviderCallContextController
 * @see useProviderCallContext
 */
export const ProviderCallContext =
  createContext<ProviderCallContextValue | null>(null);
